import { Localized, useLocalization } from "@fluent/react";
import { useAppDispatch, useDataGridCustomLocaleText } from "../../../app/hooks";
import { ProjectProcess, ProjectProcessHeader } from "../../project/projectProcess/ProjectProcessApiClient";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import {  Button, ButtonGroup, Paper, TableContainer } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { FlexDiv } from "../../../app/Utils";
import { AddFabButton } from "../../../app/components/AddFabButton";
import { unwrapResult } from "@reduxjs/toolkit";
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar";
import { loadValidationProcesses } from "./validationProcessApi";
import ValidationProcessWizard from "./ValidationProcessWizard";
import produce from "immer";
import { showSuccess } from "../../notifications/notificationsSlice";
import { ProcessNumberBox } from "../../../app/components/ProcessNumberBox";
import { ValidationProcessScenarioChip } from "./components/ValidationProcessScenarioChip";
import { StructureName } from "../../device/Structure";
import { ValidationProcessStatusPill } from "./components/ValidationProcessStatusPill";
import { defaultValidationProcessFilters, ValidationProcessFilterPanel } from "./components/ValidationProcessFilterPanel";
import { validationProcessFiltersToQueryString } from "./validationProcessFilterHelper";
import { entityRoutes } from "../../../app/components/EntityBreadcrumbs";

export const ValidationProcessList = () => {
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [processes, setProcesses] = useState<ProjectProcessHeader[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
    const localeText = useDataGridCustomLocaleText();
    const [openWizard, setOpenWizard] = useState(false);
    const [filters, setFilters] = useState(defaultValidationProcessFilters);
    const filterQuery = useMemo(() => validationProcessFiltersToQueryString(filters), [filters])

    const loadProcesses = useCallback((filter: string) => {
        setIsLoading(true);
        setIsError(false);
        setError(null);
        dispatch(loadValidationProcesses({ filter }))
            .then(unwrapResult)
            .then(setProcesses)
            .catch((error) => {
                console.error(error);
                setIsError(true);
                setError(error);
            })
            .finally(() => {
                setIsLoading(false)
                setHasLoadedOnce(true)
            });
    }, [dispatch])

    const reload = useCallback(() => {
        loadProcesses(filterQuery)
    }, [filterQuery, loadProcesses])

    useEffect(() => {
        loadProcesses(filterQuery)
    }, [filterQuery, loadProcesses])

    const onSave = (process: ProjectProcess) => {
        setProcesses(prev =>
            produce(prev, draft => {
                const idx = draft.findIndex(o => o.id === process.id);
                if (idx >= 0) {
                    draft[idx] = { ...draft[idx], ...process };
                } else {
                    draft.unshift(process);
                }
            })
        );

        setOpenWizard(false);
        dispatch(showSuccess("saved"));
        navigate(entityRoutes.process(String(process.id), "list"))
    };


    const handleGoToClick = (id) => {
        navigate(entityRoutes.process(String(id), "list"))
    };

    const processColumns: GridColDef[] = [
        {
            field: 'no',
            headerName: l10n.getString('process-no', null, '_Numer Procesu'),
            flex: 0.5,
            minWidth: 100,
            renderCell: (params) => (
                <ProcessNumberBox value={params.value} />
            ),
        },
        {
            field: 'name',
            headerName: l10n.getString('process-name', null, '_Proces'),
            flex: 1
        },
        {
            field: 'structureId',
            headerName: l10n.getString("structure", null, "_structure"),
            flex: 1,
            minWidth: 200,

            renderCell: (params) => {
                return (
                    <StructureName structureId={params.row.structureId} />
                )
            }
        },
        {
            field: 'scenario',
            headerName: l10n.getString('process-scenario', null, '_Scenariusz'),
            width: 160,
            minWidth: 160,
            renderCell: (params) => (
                <ValidationProcessScenarioChip
                    scenario={params.value}
                    size="small"
                    dense
                    truncate
                />
            ),
        },
        {
            field: 'startDate',
            headerName: l10n.getString("process-date-from-short", null, "_Start"),
            description: l10n.getString("process-date-from", null, "_Data rozpoczęcia"),
            align: 'center',
            headerAlign: 'center',
            width: 130,
            minWidth: 120,
            maxWidth: 150,
            valueGetter: (_, row) =>
                row.startDate ? new Date(row.startDate).toISOString().split('T')[0] : "-",
        },
        {
            field: 'finishDate',
            headerName: l10n.getString("process-date-to-short", null, "_Koniec"),
            description: l10n.getString("process-date-to", null, "_Data zakończenia"),
            align: 'center',
            headerAlign: 'center',
            width: 130,
            minWidth: 120,
            maxWidth: 150,
            valueGetter: (_, row) =>
                row.finishDate ? new Date(row.finishDate).toISOString().split('T')[0] : "-",
        },


        {
            field: 'status',
            headerName: l10n.getString('status', null, '_Status'),
            width: 160,
            minWidth: 160,
            maxWidth: 160,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            resizable: false,
            renderCell: (params) => (
                <div>
                    <ValidationProcessStatusPill status={params.row.status ?? ""} size="small"
                        dense />
                </div>
            )
        },
        {
            field: 'actions', type: 'actions', width: 200,
            renderCell: (params) => {
                return (
                    <ButtonGroup>
                        <Button onClick={() => handleGoToClick(params.id)} color="secondary" variant="outlined">
                            <Localized id="go-to">
                                <span>Edytuj</span>
                            </Localized>
                        </Button>
                    </ButtonGroup>
                )
            }
        }
    ];

    if (isLoading && !hasLoadedOnce) {
        return <>
            <LoadingContainer />
        </>
    }


    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    return (
        <>
            <TableContainer component={Paper}>
                <ValidationProcessFilterPanel filters={filters} onFilterChange={setFilters} reload={reload} isLoading={isLoading} />
                <div style={{ display: 'flex', height: '100%' }}>
                    <div style={{ flexGrow: 1 }}>
                        <DataGrid
                            style={{ minHeight: 100, height: 'calc(100vh - 200px)' }}
                            loading={isLoading}
                            localeText={localeText}
                            rows={processes}
                            columns={processColumns}
                            slots={{ toolbar: QuickSearchToolbar }}
                            initialState={{
                                sorting: { sortModel: [{ field: 'startDate', sort: 'desc' }] }
                            }}
                            onRowDoubleClick={({ row }) => handleGoToClick(row.id)} />
                    </div>
                </div>
                <FlexDiv>
                    <AddFabButton
                        onClick={() => setOpenWizard(true)}
                    />
                </FlexDiv>
            </TableContainer>
            {openWizard && (
                <ValidationProcessWizard
                    onClose={() => setOpenWizard(false)}
                    onSave={onSave} />
            )}
        </>
    )
}
